import { NegotiationYearSelect } from "@/components/data-driven-inputs/selects/negotiation-year-select/NegotiationYearSelect";
import { PageLayout } from "@/components/page-layout/PageLayout";
import { SpaceValues } from "@/lib/utils/SpaceValues";
import { Column, Row, Space, Text } from "@stenajs-webui/core";
import {
  Banner,
  Card,
  CardHeader,
  FlatButton,
  ResultListBanner,
} from "@stenajs-webui/elements";
import { SwitchWithLabel } from "@stenajs-webui/forms";
import { ErrorScreen, LoadingPanel } from "@stenajs-webui/panels";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CustomerInfoPairBox } from "@/features/product-and-price/info-boxes/customer/CustomerInfoPairBox";
import { expectedVolumeSelectors } from "../redux/ExpectedVolumeRedux";
import { expectedVolumeLoad } from "../redux/thunks/ExpectedVolumeLoad";
import { ExpectedVolumeForRoutePair } from "./ExpectedVolumeForRoutePair";
import { ExpectedVolumeHeader } from "./ExpectedVolumeHeader";
import { ExpectedVolumeActionButtons } from "./ExpectedVolumeActionButtons";
import { useUpdateExpectedVolumesMutation } from "../hooks/UseUpdateExpectedVolumesMutation";
import { getExpectedVolumeChanges } from "../lib/ChangesCollector";
import { StoreState } from "@/redux/RootReducer";
import { transformChangesToInput } from "../lib/ChangesTransformer";
import {
  ExpectedVolumeCollectionItem,
  ExpectedVolumeCustomer,
  ExpectedVolumeRoutePair,
} from "../types";
import { ApolloError } from "@apollo/client";
import { sortBy } from "lodash";

interface Props {
  customerId: string;
  year: number;
  setYear: (value: number) => void;
  expectedVolumeByRoute?: ExpectedVolumeCollectionItem[];
  customer?: ExpectedVolumeCustomer;
  routePairsWithStatusOwn?: ExpectedVolumeRoutePair[];
  error?: ApolloError;
  loading: boolean;
}

export const CustomerExpectedVolumePane: React.FC<Props> = ({
  customerId,
  year,
  setYear,
  expectedVolumeByRoute,
  customer,
  routePairsWithStatusOwn,
  error,
  loading,
}) => {
  const dispatch = useDispatch();
  const [includeRoutesWithNoVolume, setIncludeRoutesWithNoVolume] =
    useState(false);

  const {
    updateExpectedVolumes,
    loading: updatingExpectedVolumes,
    errors: errorsUpdating,
  } = useUpdateExpectedVolumesMutation();

  const expectedVolumesByRoutePair = useSelector(
    (state: StoreState) => state.expectedVolume.expectedVolumesByRoutePair
  );

  useEffect(() => {
    if (expectedVolumeByRoute) {
      dispatch(expectedVolumeLoad(expectedVolumeByRoute));
    }
  }, [dispatch, expectedVolumeByRoute]);

  const showEmptyState = useSelector(
    expectedVolumeSelectors.getExpectedVolumesAreAllEmpty
  );

  const onSave = async () => {
    const changes = getExpectedVolumeChanges(expectedVolumesByRoutePair);
    await updateExpectedVolumes(
      transformChangesToInput(
        year,
        customer?.statisticalGrouping?.id ?? "0",
        changes
      )
    );
  };

  const bannerState = useMemo(() => {
    if (errorsUpdating && errorsUpdating.length > 0) {
      return {
        headerText: "Could not save expected volumes",
        items: errorsUpdating.map((e) => ({
          text: e,
        })),
      };
    }
  }, [errorsUpdating]);

  const allRoutePairs = sortBy(routePairsWithStatusOwn ?? [], (a) => a.code);
  const statisticalGrouping = customer?.statisticalGrouping?.id ?? "0";

  return (
    <>
      <ExpectedVolumeHeader
        customerId={customerId}
        customer={customer}
        loading={loading}
        contentRight={
          <ExpectedVolumeActionButtons
            onSave={onSave}
            saving={updatingExpectedVolumes}
          />
        }
        contentUnder={
          bannerState && (
            <ResultListBanner bannerState={bannerState} variant={"error"} />
          )
        }
      />

      <PageLayout>
        {customer && <CustomerInfoPairBox customer={customer} />}
        <Card>
          <CardHeader
            text={"Routes"}
            contentRight={
              <Row alignItems={"center"}>
                <Text>Negotiation year</Text>
                <Space />
                <Column width={100}>
                  <NegotiationYearSelect
                    value={year}
                    onValueChange={(value) => setYear(value)}
                  />
                </Column>
                <Space num={SpaceValues.NINETYTWO} />
                <SwitchWithLabel
                  label={"Include routes with no volume"}
                  value={includeRoutesWithNoVolume}
                  onValueChange={(value) => setIncludeRoutesWithNoVolume(value)}
                />
              </Row>
            }
          />
        </Card>
        {loading ? (
          <LoadingPanel />
        ) : error ? (
          <ErrorScreen text={error.message} />
        ) : !includeRoutesWithNoVolume && showEmptyState ? (
          <Row alignItems={"center"} justifyContent={"center"}>
            <Banner
              variant={"info"}
              text={"No expected volume matches the current filter"}
            >
              <Row flex={1} justifyContent={"flex-end"}>
                <FlatButton
                  onClick={() => setIncludeRoutesWithNoVolume(true)}
                  label={"Include routes with no volume"}
                />
              </Row>
            </Banner>
          </Row>
        ) : null}

        {allRoutePairs.map((routePair) => (
          <ExpectedVolumeForRoutePair
            selectedYear={year}
            key={routePair.id}
            statNo={statisticalGrouping}
            routePairCode={routePair.id}
            hideIfNoItems={!includeRoutesWithNoVolume}
          />
        ))}
      </PageLayout>
    </>
  );
};
